import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { DropDownContainer } from './DropDownContainer';
import { DropDownItems } from './DropDownItems';
import { getActiveMenuByRoute, Menus } from './Menus';
import { SideBarContainer } from './SideBarContainer';
import { SideBarMenuContainer } from './SideBarMenuContainer';

import { DropDownIcon, TuosLogoFull } from '@components/Icons';

export const SideBar = () => {
  const [activeMenu, setActiveMenu] = useState(
    localStorage.getItem('activeMenu') || null
  );
  const [activeSubMenu, setActiveSubMenu] = useState<string | boolean>(false);

  const location = useLocation();

  useEffect(() => {
    const { activeMenu: newMenu, activeSubMenu: newSub } = getActiveMenuByRoute(
      location.pathname
    );

    setActiveMenu(newMenu);
    setActiveSubMenu(newSub);

    if (newMenu) {
      localStorage.setItem('activeMenu', newMenu);
    }
  }, [location.pathname]);

  const handleMenuClick = (menu: any) => {
    const isSame = activeMenu === menu;
    const nextState = isSame ? null : menu;
    setActiveMenu(nextState);
    localStorage.setItem('activeMenu', nextState || '');

    if (activeMenu === menu) {
      setActiveMenu(null);
      localStorage.setItem('activeMenu', '');
    } else {
      setActiveMenu(menu);
      localStorage.setItem('activeMenu', menu);
    }
  };

  const handleSubMenuClick = (menu: any, mainMenu: any) => {
    setActiveSubMenu(menu);
    setActiveMenu(mainMenu);
  };

  const activeSubMenuClass = (menu: any) =>
    activeSubMenu === menu
      ? 'flex py-2 text-xs text-white pr-4 pl-8 w-full space-x-2 rounded-lg bg-primary-500'
      : 'flex py-2 w-full text-xs pr-4 pl-8 text-primary-500 hover:text-white space-x-2 rounded-lg hover:bg-primary-500';

  const activeMenuClass = (menu: any) =>
    activeMenu === menu
      ? 'shadow-md text-black bg-green-50 py-0.5 font-bold text-[14px] pl-8 pr-4 w-full space-y-3 rounded-xl'
      : 'hover:shadow-md text-[#2B461B] fill-[#27401A] pr-4 pl-8 py-0.5 hover:bg-green-50 w-full font-md text-[14px] space-y-3 rounded-xl';

  return (
    <>
      <SideBarContainer>
        <div className="flex justify-center items-center gap-x-2 bg-[#61DBA4] h-[69px]">
          <TuosLogoFull width="104" height="30" />
        </div>

        <div className="flex-1 pb-8 space-y-2 pt-24">
          {Menus.map((menu) => {
            const Icon = menu.icon;
            const mainMenu = menu.mainMenu.replace(/\s+/g, '-').toLowerCase();

            const hasSubMenu =
              Array.isArray(menu.subMenu) && menu.subMenu.length > 0;
            const isExpanded = activeMenu === mainMenu;

            return (
              <SideBarMenuContainer key={mainMenu}>
                {hasSubMenu ? (
                  <div
                    className="w-full"
                    onClick={() => handleMenuClick(mainMenu)}
                  >
                    <div className={`${activeMenuClass(mainMenu)}`}>
                      <div className="flex justify-between items-center space-x-4">
                        <div className="flex space-x-4 items-center h-[48px]">
                          <Icon className="w-6 text-primary-500" />
                          <h1 className="flex items-center">{menu.mainMenu}</h1>
                        </div>
                        <div
                          className={`transform transition-transform duration-200
                              ${isExpanded ? 'rotate-180' : ''}`}
                        >
                          <DropDownIcon height="12" width="8" />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    onClick={() => handleMenuClick(mainMenu)}
                    to={menu.route || ''}
                    className="block no-underline"
                  >
                    <div
                      className={`${activeMenuClass(
                        mainMenu
                      )} flex items-center min-h-[48px]`}
                    >
                      <div className="flex items-center space-x-4">
                        <Icon className="w-6 text-primary-500" />
                        <h1 className="text-[14px] leading-none">
                          {menu.mainMenu}
                        </h1>
                      </div>
                    </div>
                  </Link>
                )}

                {hasSubMenu && isExpanded && (
                  <DropDownContainer>
                    {menu.subMenu?.map((sub: any) => {
                      const subMenu = sub.subMenu
                        .replace(/\s+/g, '-')
                        .toLowerCase();
                      return (
                        <DropDownItems
                          key={subMenu}
                          to={sub.subMenuRoutes}
                          onClick={() => handleSubMenuClick(subMenu, mainMenu)}
                          className={`${activeSubMenuClass(subMenu)} mb-1`}
                        >
                          {sub.subMenu}
                        </DropDownItems>
                      );
                    })}
                  </DropDownContainer>
                )}
              </SideBarMenuContainer>
            );
          })}
        </div>

        <div className="bg-primary-100/25 text-xs text-primary-500 px-8 py-4 space-y-2">
          <div className="flex space-x-2 italic">
            <EnvelopeIcon className="w-6" />
            <button
              className="hover:text-primary-800 hover:underline"
              onClick={() =>
                (window.location.href = 'mailto:support@simpacc.ca')
              }
            >
              support@simpacc.ca
            </button>
          </div>
        </div>
      </SideBarContainer>
    </>
  );
};
